import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      {/* Header */}
      <header className="privacy-header">
        <div className="container">
          <Link to="/" className="back-button">← Back to Home</Link>
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: February 27, 2026</p>
        </div>
      </header>

      {/* Content */}
      <div className="privacy-content">
        <div className="container">
          <section className="privacy-section">
            <h2>Introduction</h2>
            <p>
              Welcome to RunningAI Pro. We are committed to protecting your privacy and ensuring the security of your personal information.
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Information We Collect</h2>

            <h3>Personal Information</h3>
            <p>When you create an account with RunningAI Pro, we collect:</p>
            <ul>
              <li>Email address (for account authentication)</li>
              <li>User profile information (name, optional profile details)</li>
              <li>Account credentials (securely encrypted)</li>
            </ul>

            <h3>Running Activity Data</h3>
            <p>To provide our core services, we collect:</p>
            <ul>
              <li>GPS location data during runs (route coordinates, mile markers)</li>
              <li>Running metrics (distance, duration, pace, calories burned)</li>
              <li>Run history and performance statistics</li>
              <li>Weather conditions at the time of your runs</li>
            </ul>

            <h3>AI Interaction Data</h3>
            <p>When you enable AI features, the following data is sent to OpenAI to generate personalized responses:</p>
            <ul>
              <li>Chat messages you send to the AI coach</li>
              <li>Run history: distance, duration, pace, calories burned, and break time</li>
              <li>Training performance metrics: weekly mileage, pace trends, longest run, and consistency scores</li>
              <li>Race goals, target times, and fitness level information you provide</li>
              <li>GPS coordinates (used solely to fetch local weather conditions via Open-Meteo)</li>
            </ul>
            <p>AI features are optional. You can enable or disable data sharing with OpenAI at any time in the app's Profile &gt; Privacy &amp; AI Data settings.</p>

            <h3>Device Information</h3>
            <ul>
              <li>Device type and operating system version</li>
              <li>App version information</li>
              <li>Crash logs and diagnostic data (to improve app stability)</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>

            <div className="use-case">
              <h4>Provide Core Services</h4>
              <ul>
                <li>Track and record your running activities</li>
                <li>Display your run history and analytics</li>
                <li>Generate personalized AI coaching responses</li>
                <li>Create custom training plans based on your fitness level and goals</li>
                <li>Predict race times using your performance data</li>
              </ul>
            </div>

            <div className="use-case">
              <h4>Improve User Experience</h4>
              <ul>
                <li>Analyze app usage to improve features and performance</li>
                <li>Fix bugs and technical issues</li>
                <li>Provide customer support</li>
              </ul>
            </div>

            <div className="use-case">
              <h4>Account Management</h4>
              <ul>
                <li>Authenticate users and maintain account security</li>
                <li>Manage subscription status and premium features</li>
                <li>Send important account notifications</li>
              </ul>
            </div>
          </section>

          <section className="privacy-section">
            <h2>Data Storage and Security</h2>
            <p>
              We take the security of your data seriously. Your information is stored using industry-standard security measures:
            </p>
            <ul>
              <li>All data is encrypted in transit using HTTPS/TLS protocols</li>
              <li>User authentication is handled through Supabase with secure password hashing</li>
              <li>Personal data is stored on secure cloud servers with access controls</li>
              <li>We implement regular security audits and updates</li>
              <li>Payment information is processed through Apple's In-App Purchase system and we do not store your payment details</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Location Data</h2>
            <p>
              RunningAI Pro uses GPS location data to track your runs. Please note:
            </p>
            <ul>
              <li>Location data is only collected when you actively start a run</li>
              <li>You can control location permissions through your device settings</li>
              <li>Your GPS route coordinates are stored securely in our database to display your run history and calculate metrics</li>
              <li>Your GPS coordinates (latitude and longitude) are shared with Open-Meteo to fetch local weather conditions during runs — no personal identifiers are included</li>
              <li>If you have enabled AI features, your GPS coordinates may be included in data sent to OpenAI solely to provide weather context for run analysis</li>
              <li>You can delete any run, which will permanently remove the associated location data</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Third-Party Services</h2>
            <p>RunningAI Pro integrates with the following third-party services:</p>

            <div className="third-party">
              <h4>OpenAI (openai.com)</h4>
              <p>
                We use OpenAI's API to power AI coaching features. AI features are <strong>opt-in only</strong> — data is
                only sent to OpenAI after you explicitly consent during account registration or enable it in Profile &gt; Privacy &amp; AI Data.
              </p>
              <p>When AI features are enabled, the following data is sent to OpenAI:</p>
              <ul>
                <li>Chat messages you send to the AI coach</li>
                <li>Run history (distance, duration, pace, calories, break time)</li>
                <li>Training performance metrics (weekly mileage, pace trends, longest run, consistency scores)</li>
                <li>Race goals, target times, and fitness level information</li>
                <li>GPS coordinates (to provide weather context for run analysis)</li>
              </ul>
              <p>
                OpenAI does not use data submitted via the API to train their models by default. Your data is governed by
                OpenAI's <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
                <a href="https://openai.com/policies/api-data-usage-policies" target="_blank" rel="noopener noreferrer">API Data Usage Policies</a>,
                which provide equal or greater data protection. You can disable AI data sharing at any time in the app.
              </p>
            </div>

            <div className="third-party">
              <h4>Supabase</h4>
              <p>
                We use Supabase for authentication and database services. Your account information and running data are
                stored on Supabase's secure infrastructure.
              </p>
            </div>

            <div className="third-party">
              <h4>Apple Services</h4>
              <p>
                In-app purchases and subscriptions are processed through Apple's App Store, subject to Apple's privacy policy.
              </p>
            </div>

            <div className="third-party">
              <h4>Open-Meteo (open-meteo.com)</h4>
              <p>
                We use the Open-Meteo API to fetch weather conditions during your runs. Your GPS coordinates (latitude and
                longitude) are sent as URL parameters to retrieve local weather data. No account information or personal
                identifiers are shared. Open-Meteo is a free, open-source weather API that does not store or sell location data.
              </p>
            </div>
          </section>

          <section className="privacy-section">
            <h2>Data Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share your data only in the following circumstances:</p>
            <ul>
              <li><strong>With your consent:</strong> When you choose to share your runs or achievements with the community</li>
              <li><strong>Service providers:</strong> With third-party services that help us operate the app (as listed above)</li>
              <li><strong>Legal requirements:</strong> If required by law, court order, or governmental regulation</li>
              <li><strong>Safety and security:</strong> To protect the rights, property, or safety of RunningAI Pro, our users, or others</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>Your Rights and Choices</h2>
            <p>You have the following rights regarding your data:</p>

            <div className="user-right">
              <h4>Access and Export</h4>
              <p>You can view all your running data and account information within the app at any time.</p>
            </div>

            <div className="user-right">
              <h4>Delete Data</h4>
              <p>You can delete individual runs or your entire account. Account deletion will permanently remove all your data from our servers.</p>
            </div>

            <div className="user-right">
              <h4>Opt-Out of AI Data Sharing</h4>
              <p>
                You can disable AI data sharing with OpenAI at any time by going to Profile &gt; Privacy &amp; AI Data and
                toggling off "Share data with OpenAI". When disabled, no data will be sent to OpenAI and AI coaching
                features will be unavailable until you re-enable it.
              </p>
            </div>

            <div className="user-right">
              <h4>Opt-Out of Location Tracking</h4>
              <p>You can disable location permissions in your device settings. Note that this will limit the app's run tracking functionality.</p>
            </div>

            <div className="user-right">
              <h4>Marketing Communications</h4>
              <p>You can opt out of promotional emails by following the unsubscribe link or adjusting your notification settings.</p>
            </div>

            <div className="user-right">
              <h4>Data Portability</h4>
              <p>You can request a copy of your data in a machine-readable format by contacting our support team.</p>
            </div>
          </section>

          <section className="privacy-section">
            <h2>Children's Privacy</h2>
            <p>
              RunningAI Pro is not intended for children under the age of 13. We do not knowingly collect personal information
              from children under 13. If you are a parent or guardian and believe your child has provided us with personal
              information, please contact us so we can delete such information.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Data Retention</h2>
            <p>We retain your information for as long as your account is active or as needed to provide you services. You may request deletion of your data at any time by:</p>
            <ul>
              <li>Deleting your account through the app settings</li>
              <li>Contacting our support team at <a href="mailto:support@runningai.app">support@runningai.app</a></li>
            </ul>
            <p>After account deletion, some information may be retained in backups for up to 30 days before permanent deletion.</p>
          </section>

          <section className="privacy-section">
            <h2>International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence.
              These countries may have data protection laws that differ from your country. We ensure appropriate safeguards
              are in place to protect your information in accordance with this Privacy Policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by:
            </p>
            <ul>
              <li>Posting the new Privacy Policy within the app</li>
              <li>Updating the "Last Updated" date at the top of this policy</li>
              <li>Sending you a notification for material changes</li>
            </ul>
            <p>Your continued use of RunningAI Pro after changes become effective constitutes acceptance of the revised policy.</p>
          </section>

          <section className="privacy-section">
            <h2>Contact Us</h2>
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
            <div className="contact-info">
              <p><strong>Email:</strong> <a href="mailto:support@runningaipro.com">support@runningaipro.com</a></p>
              <p><strong>Support:</strong> Available through the app's Help section</p>
            </div>
          </section>

          <section className="privacy-section consent-section">
            <h2>Your Consent</h2>
            <p>
              By using RunningAI Pro, you consent to this Privacy Policy and agree to its terms. If you do not agree with
              this policy, please do not use our application.
            </p>
            <p>
              <strong>AI Features Require Separate Consent:</strong> Sharing your personal data with OpenAI for AI coaching
              features requires your explicit opt-in consent, which is requested during account registration. You may
              withdraw this consent at any time in Profile &gt; Privacy &amp; AI Data. Withdrawing consent will immediately
              stop all data sharing with OpenAI and disable AI coaching features.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="privacy-footer">
        <div className="container">
          <p>&copy; 2026 RunningAI Pro. All rights reserved.</p>
          <div className="footer-links">
            <Link to="/">Home</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/support">Support</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
